import React, { useState } from 'react';
import { MessageSquarePlus } from 'lucide-react';
import { errorMessage } from '../api';
import { Dialog, Field, FormError } from '../components';
import { kontingentAnfragen } from '../kontingent';
import { useAuth } from '../../contexts/AuthContext';
import { GUTHABEN_TEXTE } from './guthabenTexte';

/** GA-10 auf den Seiten der Person (Startseite, „Mein Zugang“, „Nutzung“): Besitzer, Zahlrecht (account.canBuyTopUp) oder wer das
 *  Kontingent der Firma sieht und verteilt (verwaltet: in der Verwaltung costs.write, auf den Seiten der Person verteilungSichtbar);
 *  nie in einer Sicht. Der Server prüft dasselbe und schickt anfrageOffen nur an Konten, die anfragen dürfen. */
export function useAnfrageDarf(verwaltet?: boolean): boolean {
  const { user } = useAuth();
  if (user?.sicht?.aktiv) return false;
  return user?.account?.canBuyTopUp === true || user?.account?.rolle?.art === 'BESITZER' || verwaltet === true;
}

// Spur guthaben, Gruppe firma, S3-0 (SPEC-KONTINGENT.md KO-E2, 14.1 Punkt 9; welle-2/guthaben/AUFTEILUNG.md 2.4, W-S6, GA-9,
// GA-10): der Knopf „Beim Anbieter mehr anfragen …“ mit seinem kurzen Fenster. Er steht neben jedem Satz mit dem Weg zum
// Anbieter (KT-24 für die Verwaltung, KT-35, KT-41, KT-50, KT-55) und neben „Guthaben aufladen …“, nie für Mitglieder ohne
// Verwaltungsrecht, nie in einer Sicht und nie im Fenster „Mehr für {Name} – nur diesen Monat“ (GA-9, kein Fenster im Fenster).
// Die Anfrage geht über den vorhandenen Weg der Verwaltung zum Anbieter (POST /billing/kontingent-anfrage, kein Mailversand);
// die Antwort nennt keinen Betrag und keine Zahl. Ist schon eine Anfrage offen (anfrageOffen), steht statt des Knopfs die
// Bestätigung. Ohne Angabe des Servers (anfrageOffen fehlt: Konto darf nicht anfragen oder der Server kennt den Weg noch nicht)
// und ohne Recht (darf, entscheidet die Seite) steht nichts.
export function AnbieterAnfrage({ organizationId, anfrageOffen, darf = true, klein = false, onGesendet }: {
  organizationId: string | null | undefined;
  anfrageOffen: boolean | null | undefined;
  /** Besitzer, „Kontingent verteilen“ oder Zahlrecht und keine Sicht (GA-10); der Server prüft dasselbe. */
  darf?: boolean;
  /** Textlink statt Knopf, für eine Zeile mit Satz (KT-41, KT-50). */
  klein?: boolean;
  onGesendet?: () => void;
}) {
  const [offen, setOffen] = useState(false), [gesendet, setGesendet] = useState(false);
  if (!darf || !organizationId || anfrageOffen === undefined || anfrageOffen === null) return null;
  if (gesendet) return <p className="adm-success" role="status" data-anfrage="gesendet">{GUTHABEN_TEXTE.anfrageBestaetigung}</p>;
  if (anfrageOffen) return <p className="adm-info" data-anfrage="offen">{GUTHABEN_TEXTE.anfrageBestaetigung}</p>;
  return <>
    <button type="button" className={klein ? 'adm-text-link' : 'adm-button'} onClick={() => setOffen(true)}>
      {!klein && <MessageSquarePlus size={16} aria-hidden="true" />}{GUTHABEN_TEXTE.anfrageKnopf}
    </button>
    {offen && <AnfrageFenster organizationId={organizationId} onClose={() => setOffen(false)}
      onFertig={() => { setOffen(false); setGesendet(true); onGesendet?.(); }} />}
  </>;
}

function AnfrageFenster({ organizationId, onClose, onFertig }: { organizationId: string; onClose: () => void; onFertig: () => void }) {
  const [text, setText] = useState(''), [busy, setBusy] = useState(false), [fehler, setFehler] = useState('');
  const senden = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true); setFehler('');
    try {
      const frei = text.trim();
      await kontingentAnfragen({ organizationId, ...(frei ? { text: frei } : {}) });
      onFertig();
    } catch (error) { setFehler(errorMessage(error)); }
    finally { setBusy(false); }
  };
  return <Dialog title={GUTHABEN_TEXTE.anfrageTitel} onClose={onClose} busy={busy} verwerfenFragen={text.trim() !== ''}>
    <form onSubmit={senden}>
      <Field label={GUTHABEN_TEXTE.anfrageFrage}><textarea rows={4} maxLength={1000} value={text} onChange={event => setText(event.target.value)} /></Field>
      <FormError message={fehler} />
      <footer>
        <button className="adm-button" type="button" onClick={onClose} disabled={busy}>{GUTHABEN_TEXTE.abbrechen}</button>
        <button className="adm-button adm-primary" type="submit" disabled={busy}>{busy ? GUTHABEN_TEXTE.anfrageSendet : GUTHABEN_TEXTE.anfrageSenden}</button>
      </footer>
    </form>
  </Dialog>;
}
